import type {LocationSuggestion} from '../types/custom';
import {getDefaultCitiesSuggestionsForLocale, defaultCitiesSuggestions} from '@malt/location-autocomplete/get-default-cities-suggestions';
import {useLocale, useUniversalFetch} from '#imports';
import {getCountryCentroid} from './countryCentroids';

type BuildOptions = {
    remoteLabel?: string | null;
    globalMatching: boolean;
    countryCode?: string | null;
    buildCountrySelectLabel: (resolvedCountryLabel: string) => string; // TODO => Remove this
};

type CountryResource = {code: string; label: string};

async function resolveCountryLabelInternal(code: string): Promise<string> {
    try {
        const currentLocale = useLocale();
        const regionNames = new Intl.DisplayNames(currentLocale.languageCode.value, {type: 'region'});
        return regionNames.of(code) ?? code;
    } catch (e) {
        try {
            const country = await useUniversalFetch<CountryResource>(`/api/countries/${code}`);
            return country.label;
        } catch (e2) {
            return code;
        }
    }
}

export async function buildDefaultLocationSuggestions(options: BuildOptions): Promise<LocationSuggestion[]> {
    const {remoteLabel, globalMatching: globalMatchingEnabled, countryCode, buildCountrySelectLabel} = options;

    const result: LocationSuggestion[] = [];

    if (remoteLabel && remoteLabel !== '') {
        result.push({formattedAddress: remoteLabel, remoteAllowed: true} as any);
    }

    if (globalMatchingEnabled) {
        const base: LocationSuggestion[] = [...defaultCitiesSuggestions.globalMatching];

        if (countryCode) {
            const label = await resolveCountryLabelInternal(countryCode);
            const centroid = getCountryCentroid(countryCode);
            base.push({
                country: label,
                city: label,
                loc: centroid ? {lat: centroid.lat, lon: centroid.lon} : {lat: 0, lon: 0},
                countryCode,
                formattedAddress: label,
                formattedAddressHtml: buildCountrySelectLabel(label),
            } as any);
        }

        result.push(...base);
        return result;
    }

    const {locale} = useLocale();
    result.push(...getDefaultCitiesSuggestionsForLocale(locale.value));
    return result;
}
